import { Box, CheckBox } from "@dealer-center/core-components";
import type { AccountPermissionNode } from "@dealer-center/ddc-pages-configuration-interface";

export interface AccountNodeProps {
  node: AccountPermissionNode;
  /** 1-based, matching `aria-level`. Defaults to a root. */
  depth?: number;
}

/**
 * One account in the hierarchy, and — recursively — everything beneath it.
 *
 * **Recursive with no depth assumption** (FR-029). Live data reaches three levels
 * (`salesattributiongroup → salesattributiona → salesattributiona1`), and CC9 imposes no bound, so
 * the component recurses on `children` rather than special-casing a parent/child pair. `children` is
 * present on every live node and `[]` for leaves — the swagger omits the field entirely, so the
 * live-verified shape in `interface/` is the one to trust.
 *
 * **Nesting is conveyed programmatically, not by indentation** (FR-050): real nested lists plus an
 * explicit `aria-level`. An `sx` margin alone communicates nothing to a screen reader, and the
 * contract warns that indentation stops being legible past 4 levels anyway — so the indent is kept
 * small and the structure carries the meaning. Nested lists also keep the parent relationship
 * recoverable without horizontal scrolling.
 *
 * **Row content is name *and* identifier** (FR-028), combined into the control's accessible name so
 * one control carries both. A node whose name cannot be resolved falls back to the identifier alone —
 * never a blank row, never an error (Edge Cases). CC9 returns `""` rather than null for unset strings,
 * so that fallback is the realistic path, not a defensive one.
 *
 * **Every control is disabled** (FR-030), unconditionally. `writeAccess` supplies the checked state
 * and nothing else: it is display data about the subject and must not gate anything (Monorepo
 * Principle VII). Granting accounts is User Manager's job.
 */
export const AccountNode = ({ node, depth = 1 }: AccountNodeProps) => {
  const name = node.name?.trim();
  // FR-028's two values in one accessible name. Identifier alone when the name is unresolvable.
  const label = name ? `${name} (${node.accountId})` : node.accountId;
  /*
   * `children` is optional and the payload is unvalidated, so an absent `children` must render as a
   * leaf rather than throw. Normalised once here instead of `?.` at each of the two use sites, so the
   * recursive `.map` below cannot be reached with `undefined`.
   */
  const children = node.children ?? [];
  const hasChildren = children.length > 0;

  return (
    <Box
      aria-level={depth}
      component={"li"}
      data-testid={`account-node-${node.accountId}`}
      /*
       * `paddingY` gives each row air. Without it the rows sit at the checkbox's own line height and
       * a hierarchy of ten accounts reads as an undifferentiated block, which is what makes the
       * indentation hard to follow — the vertical rhythm is what separates siblings, and the indent
       * only distinguishes levels once there is rhythm to indent.
       */
      sx={{ listStyle: "none", paddingY: "4px" }}
    >
      <CheckBox
        disabled
        checked={node.writeAccess}
        data-testid={`account-row-${node.accountId}`}
        id={`account-${node.accountId}`}
        label={label}
        name={node.accountId}
        value={node.accountId}
      />

      {hasChildren ? (
        /*
         * 28px per level, matching the prototype. Wide enough that a child is unambiguously indented
         * past its parent's checkbox rather than looking like a slightly-misaligned sibling — the
         * previous 20px was narrower than the checkbox-plus-gap it has to clear.
         */
        <Box
          component={"ul"}
          data-testid={`account-children-${node.accountId}`}
          sx={{ margin: 0, paddingLeft: "28px" }}
        >
          {children.map(child => (
            <AccountNode depth={depth + 1} key={child.accountId} node={child} />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
